/**
 * Utilities for building human-readable card page URLs.
 *
 * URL format: /cards/{name-slug}/{setCode}/{collectorNumber}
 * Example:    /cards/mana-crypt/2xm/270
 */

/** Convert a card name to a URL-safe slug. */
export function toCardSlug(name: string): string {
  const slug = name
    .toLowerCase()
    // Transliterate non-ASCII chars found in EN card names (audit 2026-04-03)
    .replace(/[àáâãä]/g, 'a')
    .replace(/[éè]/g, 'e')
    .replace(/[íîï]/g, 'i')
    .replace(/[óöō]/g, 'o')
    .replace(/[úûü]/g, 'u')
    .replace(/ñ/g, 'n')
    .replace(/š/g, 's')
    .replace(/æ/g, 'ae')
    .replace(/[®꞉]/g, '')
    .replace(/\s*\/\/\s*/g, '-') // " // " → hyphen (split cards)
    .replace(/\//g, '-')         // "/" → hyphen (Who/What/When/Where/Why)
    .replace(/\s+/g, '-')        // spaces → hyphens
    .replace(/[^a-z0-9-]/g, '')  // strip remaining non-alphanumeric
    .replace(/-{2,}/g, '-')      // collapse consecutive hyphens
    .replace(/^-|-$/g, '');       // trim leading/trailing hyphens

  return slug || 'card'; // fallback for Un-set cards like ______ and _____
}

interface CardUrlData {
  name: string;
  setCode?: string | null;
  collectorNumber?: string | null;
  scryfallID?: string;
  scryfallId?: string; // some interfaces use lowercase 'd'
}

/** Build the canonical card URL path from card data.
 *  Falls back to UUID URL when setCode or collectorNumber is missing. */
export function buildCardUrl(card: CardUrlData): string {
  const setCode = card.setCode;
  const collectorNumber = card.collectorNumber;

  if (setCode && collectorNumber) {
    return `/cards/${toCardSlug(card.name)}/${setCode.toLowerCase()}/${collectorNumber}`;
  }

  // Fallback to UUID URL
  const uuid = card.scryfallID || card.scryfallId;
  if (uuid) {
    return `/cards/${uuid}`;
  }

  // Last resort: search
  return `/search/results?name=${encodeURIComponent(card.name)}&exact=true`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Detect whether a URL parameter looks like a Scryfall UUID. */
export function isUuidFormat(param: string): boolean {
  return UUID_RE.test(param);
}
